import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

// Configs
import { IWarehouseGroup } from '../../../shared/interfaces';
import { IAgGridSideMenuItemConfig } from '../../../shared/interfaces/ag-grid';
import { LocalStorageKeys } from '../../../shared/interfaces/localstorage.interface';
import { MapEnumTextPipe } from '../../../shared/pipes/map-enum-text.pipe';
import { trackById } from '../../../shared/util/track-by.util';
import { StockAgTableConfig } from '../../configs/stock-ag-table.config';
import {
  EStockStatus,
  EStockStatusValue,
  IStockStatus,
} from '../../interfaces/stock.interface';

import { AgGridCustomFilterFormComponent } from './../../../shared/components/ag-grid-custom-filter-form/ag-grid-custom-filter-form.component';
import { DialogConfig } from './../../../shared/configs/dialog.config';

// Components

// Interfaces

import {
  EAgGridFilter,
  EAgGridFilterActions,
  IAgGridFilter,
} from './../../../shared/interfaces/ag-grid-filter.interface';

// Pipes

// Services
import { StockFacadeService } from './../../../shared/services/facades/stock/stock.facade.service';

// Utils

@Component({
  selector: 'lum-stock',
  templateUrl: './stock.component.html',
  styleUrls: ['./stock.component.scss'],
})
export class StockComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  canExportUnitCost :boolean = false;

  activeFilter :string | null = null;

  sideMenuItemConfig :IAgGridSideMenuItemConfig = StockAgTableConfig.sideMenuConfig;

  trackById = trackById;
  private onDestroy$ = new Subject();

  #DEFAULT_ACTIVE_FILTER :EStockStatusValue | '' = '';

  constructor(
    private dialog :MatDialog,
    private router :Router,
    private activatedRoute :ActivatedRoute,
    private stockFacadeService :StockFacadeService,
    private mapEnumTextPipe :MapEnumTextPipe,
  ) {}

  ngOnInit() :void {
    this.loadData();
    this.initActiveRoute();
  }

  ngOnChanges({ canExportUnitCost } :SimpleChanges) :void {
    if (canExportUnitCost) {
      this.stockFacadeService.setCanExportUnitCost(canExportUnitCost.currentValue);
    }
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSetStatus(status :string | null) :void {
    this.activeFilter = status;
    this.stockFacadeService.setCurrentStockStatus(status);
    this.updateUrlQueryParams(status || '');
    this.stockFacadeService.resetSelectedRows();
  }

  onSetFilter(data :{ action :string; filter :IAgGridFilter }) :void {
    const { action, filter } = data;
    switch (action) {
      case EAgGridFilterActions.SET:
        this.stockFacadeService.setCurrentCustomFilter(filter);
        this.updateUrlQueryParams(filter.name);
        break;
      case EAgGridFilterActions.EDIT:
        this.onEditFilter(filter);
        break;
      case EAgGridFilterActions.DELETE:
        this.stockFacadeService.deleteCustomFilter(filter.id);

        break;
      default:
        break;
    }
  }

  onSaveFilter() :void {
    this.dialog
      .open(AgGridCustomFilterFormComponent, {
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        if (value) {
          this.stockFacadeService.createCustomFilter({
            name: value.name,
            filters: JSON.parse(
              localStorage.getItem(LocalStorageKeys.STOCK_AG_STATE) || '{}',
            ),
            module: EAgGridFilter.STOCK,
          });
        }
      });
  }

  onEditFilter(data :IAgGridFilter) :void {
    this.dialog
      .open(AgGridCustomFilterFormComponent, {
        width: DialogConfig.sizes.small,
        data: data,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        if (value) {
          this.stockFacadeService.updateCustomFilter(data.id, {
            ...data,
            name: value.name,
          });
        }
      });
  }

  private loadData() :void {
    this.stockFacadeService.getStockStatuses();
    this.stockFacadeService.getStockCustomFilters({
      module: EAgGridFilter.STOCK,
      limit: '100',
    });
    this.stockFacadeService.getWarehouseGroups({
      limit: '100',
      relations: 'warehouses',
    });
  }

  private initActiveRoute() :void {
    this.activatedRoute.queryParams.pipe(take(1)).subscribe((params) => {
      this.activeFilter = params?.filter ?? this.#DEFAULT_ACTIVE_FILTER;

      // empty string means "all"
      if (this.activeFilter === '') {
        this.onSetStatus('');
      } else {
        const filterParam = this.activeFilter.toUpperCase();

        if (
          Object.values(EStockStatusValue).includes(
            filterParam as EStockStatusValue,
          )
        ) {
          this.onSetStatus(filterParam);
        }
      }
    });
  }

  private updateUrlQueryParams(name :string) :void {
    // Update URL with the new filter
    this.activatedRoute.queryParams.pipe(take(1)).subscribe((params) => {
      const newParams = { ...params, filter: name || null };

      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: newParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    });
  }

  get customFilters$() :Observable<IAgGridFilter[]> {
    return this.stockFacadeService?.customFilters$;
  }

  get loadingCustomFilters$() :Observable<boolean> {
    return this.stockFacadeService.loadingCustomFilters$;
  }

  get currentCustomFilter$() :Observable<any | null> {
    return this.stockFacadeService?.currentCustomFilter$ || null;
  }

  get currentStockStatus$() :Observable<string | null> {
    return this.stockFacadeService?.currentStockStatus$ || null;
  }

  get warehouseGroups$() :Observable<IWarehouseGroup[]> {
    return this.stockFacadeService?.warehouseGroups$;
  }

  get sideMenuItems() :any[] {
    const items = [
      {
        name: '',
        label: 'All',
        data: {
          stockStatus: '',
          statusCount: this.stockFacadeService.stockStatuses.reduce(
            (total :number, { stockStatusCount } :IStockStatus) =>
              total + stockStatusCount,
            0,
          ),
        },
        disabled: false,
      },
    ];

    for (const stockStatusValue of Object.values(EStockStatusValue)) {
      const facadeStatus = this.stockFacadeService.stockStatuses?.find(
        (status :any) => {
          return status.stockStatus === stockStatusValue;
        },
      );

      const stockStatus = {
        stockStatus: stockStatusValue,
        statusCount: facadeStatus?.stockStatusCount ?? 0,
      };

      items.push({
        name: stockStatus.stockStatus,
        label: this.mapEnumTextPipe.transform(
          stockStatus.stockStatus,
          EStockStatus,
        ),
        data: stockStatus,
        disabled: stockStatus.statusCount <= 0,
      });
    }

    return items;
  }

  get storedPageState() :any {
    return this.stockFacadeService.storedPageState;
  }
}
